import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Login } from '@/views/Login';
import { apiFetch } from '@/lib/auth';
import { fileToLogoDataUrl } from '@/lib/imageResize';

/**
 * Welle 9: SUPERADMIN-App via ?superadmin. Mandantenübergreifend — Mandanten
 * anlegen/konfigurieren, deaktivieren/löschen und je Mandant Admins bootstrappen.
 * Nutzt den bestehenden Login wieder; serverseitig durch assertSuperAdmin abgesichert.
 */
export default function SuperAdminApp() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('bamoa_token'));

  useEffect(() => {
    const onLogin = () => setToken(localStorage.getItem('bamoa_token'));
    const onLogout = () => setToken(null); // pre234: Auto-Logout → zurück zur Login-Maske
    window.addEventListener('bamoa:logged-in', onLogin);
    window.addEventListener('bamoa:logout', onLogout);
    return () => { window.removeEventListener('bamoa:logged-in', onLogin); window.removeEventListener('bamoa:logout', onLogout); };
  }, []);

  const logout = () => {
    ['bamoa_token', 'bamoa_user_id', 'bamoa_user_name', 'bamoa_tenant_id', 'bamoa_user_role'].forEach((k) => localStorage.removeItem(k));
    setToken(null);
  };

  if (!token) return <Login />;

  return (
    <div style={shell}>
      <div style={headerBar}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Mandanten-Administration</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', letterSpacing: '.05em', textTransform: 'uppercase' }}>SUPERADMIN</div>
        </div>
        <button onClick={logout} style={logoutBtn}>Abmelden</button>
      </div>
      <div style={contentWrap}>
        <TenantsPanel />
      </div>
    </div>
  );
}

interface Tenant {
  id: string; slug: string; name: string; enabled: boolean;
  authPolicy: string; e2eePolicy: string; createdAt: string;
  logo?: string | null;
  counts: { members: number; steles: number; groups: number; calls: number };
}
interface TenantAdmin { id: string; email: string; displayName: string; role: string; enabled: boolean }

const AUTH_OPTS = [
  { v: 'password-only', label: 'Nur Passwort' },
  { v: 'totp-optional', label: '2FA optional' },
  { v: 'totp-required', label: '2FA Pflicht' },
];
const E2EE_OPTS = [{ v: 'on', label: 'An' }, { v: 'off', label: 'Aus' }];
const authLabel = (v: string) => AUTH_OPTS.find((o) => o.v === v)?.label || v;

function TenantsPanel() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [edit, setEdit] = useState<Partial<Tenant>>({});
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  // anlegen
  const [nName, setNName] = useState('');
  const [nSlug, setNSlug] = useState('');
  const [nAuth, setNAuth] = useState('password-only');
  const [nE2ee, setNE2ee] = useState('on');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/superadmin/tenants');
      if (r.status === 403) { setDenied(true); return; }
      if (!r.ok) throw new Error();
      setTenants(await r.json());
      setErr('');
    } catch { setErr('Mandanten konnten nicht geladen werden.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const createTenant = async () => {
    setCreating(true); setCreateErr('');
    try {
      const r = await apiFetch('/api/superadmin/tenants', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name: nName, slug: nSlug, authPolicy: nAuth, e2eePolicy: nE2ee }) });
      if (r.ok) { const t = await r.json(); setTenants((xs) => [...xs, t].sort((a, b) => a.name.localeCompare(b.name))); setNName(''); setNSlug(''); }
      else { const b = await r.json().catch(() => ({})); setCreateErr(b?.message || 'Anlegen fehlgeschlagen.'); }
    } catch { setCreateErr('Verbindung fehlgeschlagen.'); }
    finally { setCreating(false); }
  };

  const patch = async (t: Tenant, body: any) => {
    setBusyId(t.id);
    try {
      const r = await apiFetch(`/api/superadmin/tenants/${t.id}`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
      if (r.ok) { const upd = await r.json(); setTenants((xs) => xs.map((x) => (x.id === t.id ? upd : x)).sort((a, b) => a.name.localeCompare(b.name))); return true; }
      const b = await r.json().catch(() => ({})); setErr(b?.message || 'Speichern fehlgeschlagen.'); return false;
    } catch { setErr('Verbindung fehlgeschlagen.'); return false; }
    finally { setBusyId(null); }
  };

  const remove = async (t: Tenant) => {
    setBusyId(t.id);
    try {
      const r = await apiFetch(`/api/superadmin/tenants/${t.id}`, { method: 'DELETE' });
      if (r.ok) { setTenants((xs) => xs.filter((x) => x.id !== t.id)); setExpandedId(null); setConfirmDel(null); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Löschen fehlgeschlagen.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const openEdit = (t: Tenant) => {
    if (expandedId === t.id) { setExpandedId(null); return; }
    setExpandedId(t.id); setConfirmDel(null);
    setEdit({ name: t.name, slug: t.slug, authPolicy: t.authPolicy, e2eePolicy: t.e2eePolicy });
  };

  if (denied) {
    return (
      <section style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Kein Zugriff</div>
        <div style={{ fontSize: 13.5, color: 'var(--muted, #8aa)' }}>
          Dieser Bereich ist SUPERADMIN-Konten vorbehalten. Dein Konto hat diese Rolle nicht.
        </div>
      </section>
    );
  }

  const totalSteles = tenants.reduce((a, t) => a + t.counts.steles, 0);
  const totalMembers = tenants.reduce((a, t) => a + t.counts.members, 0);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="Mandanten" value={tenants.length} />
        <Stat label="Nutzer gesamt" value={totalMembers} />
        <Stat label="Stelen gesamt" value={totalSteles} />
      </div>

      <section style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Mandant anlegen</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ ...fieldCol, flex: 2, minWidth: 150 }}>
            <span style={fieldLabel}>Name</span>
            <input value={nName} onChange={(e) => setNName(e.target.value)} placeholder="z. B. Sparkasse Bonn" style={input} />
          </label>
          <label style={{ ...fieldCol, flex: 1, minWidth: 130 }}>
            <span style={fieldLabel}>Slug</span>
            <input value={nSlug} onChange={(e) => setNSlug(e.target.value.toLowerCase())} placeholder="spk-bonn" style={input} />
          </label>
          <label style={{ ...fieldCol, flex: 1, minWidth: 120 }}>
            <span style={fieldLabel}>Anmeldung</span>
            <select value={nAuth} onChange={(e) => setNAuth(e.target.value)} style={input}>
              {AUTH_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
          <label style={{ ...fieldCol, flex: 1, minWidth: 90 }}>
            <span style={fieldLabel}>E2EE</span>
            <select value={nE2ee} onChange={(e) => setNE2ee(e.target.value)} style={input}>
              {E2EE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
          <button onClick={createTenant} disabled={creating || nName.trim().length < 2} style={{ ...primaryBtn, opacity: creating || nName.trim().length < 2 ? 0.5 : 1 }}>
            {creating ? 'Anlegen…' : 'Anlegen'}
          </button>
        </div>
        {createErr && <div style={{ color: '#d8785a', fontSize: 12.5, marginTop: 8 }}>{createErr}</div>}
      </section>

      <section style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Mandanten</div>
        {err && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
        {loading ? <div style={mutedLine}>Wird geladen…</div> : tenants.length === 0 ? <div style={mutedLine}>Keine Mandanten.</div> : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {tenants.map((t) => (
              <div key={t.id} style={{ borderBottom: '1px solid var(--hairline-soft)', padding: '10px 2px', opacity: t.enabled ? 1 : 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{t.name} <span style={{ fontSize: 12, color: 'var(--muted, #8aa)', fontWeight: 400 }}>· {t.slug}</span></span>
                    <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>
                      {t.counts.steles} Stelen · {t.counts.members} Nutzer · {t.counts.groups} Gruppen · {t.counts.calls} Calls · {authLabel(t.authPolicy)} · E2EE {t.e2eePolicy === 'off' ? 'aus' : 'an'}
                    </span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => patch(t, { enabled: !t.enabled })} disabled={busyId === t.id}
                      style={{ ...miniBtn, color: t.enabled ? '#16A34A' : 'var(--muted, #8aa)', borderColor: t.enabled ? 'rgba(34,197,94,0.4)' : 'var(--hairline, #232323)' }}>
                      {t.enabled ? 'Aktiv' : 'Inaktiv'}
                    </button>
                    <button onClick={() => openEdit(t)} style={miniBtn}>{expandedId === t.id ? 'Schließen' : 'Verwalten'}</button>
                  </span>
                </div>

                {expandedId === t.id && (
                  <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: 'var(--bg-elev-2)', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <div style={sectionTitle}>Einstellungen</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <label style={{ ...fieldCol, flex: 2, minWidth: 140 }}>
                          <span style={fieldLabel}>Name</span>
                          <input value={edit.name ?? ''} onChange={(e) => setEdit((x) => ({ ...x, name: e.target.value }))} style={input} />
                        </label>
                        <label style={{ ...fieldCol, flex: 1, minWidth: 120 }}>
                          <span style={fieldLabel}>Slug</span>
                          <input value={edit.slug ?? ''} onChange={(e) => setEdit((x) => ({ ...x, slug: e.target.value.toLowerCase() }))} style={input} />
                        </label>
                        <label style={{ ...fieldCol, flex: 1, minWidth: 110 }}>
                          <span style={fieldLabel}>Anmeldung</span>
                          <select value={edit.authPolicy ?? 'password-only'} onChange={(e) => setEdit((x) => ({ ...x, authPolicy: e.target.value }))} style={input}>
                            {AUTH_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                          </select>
                        </label>
                        <label style={{ ...fieldCol, flex: 1, minWidth: 80 }}>
                          <span style={fieldLabel}>E2EE</span>
                          <select value={edit.e2eePolicy ?? 'on'} onChange={(e) => setEdit((x) => ({ ...x, e2eePolicy: e.target.value }))} style={input}>
                            {E2EE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                          </select>
                        </label>
                        <button onClick={() => patch(t, edit).then((ok) => ok && setExpandedId(null))} disabled={busyId === t.id || (edit.name ?? '').trim().length < 2} style={{ ...primaryBtn, opacity: (edit.name ?? '').trim().length < 2 ? 0.5 : 1 }}>Speichern</button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                        <span style={fieldLabel}>Logo</span>
                        <div style={{ width: 96, height: 48, borderRadius: 8, border: '1px solid var(--hairline, #232323)', background: '#0e0e10', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          {t.logo ? <img src={t.logo} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 10.5, color: 'var(--muted, #8aa)' }}>keins</span>}
                        </div>
                        <label style={{ ...miniBtn, cursor: 'pointer' }}>
                          {t.logo ? 'Ersetzen' : 'Hochladen'}
                          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: 'none' }}
                            onChange={async (e) => { const f = e.target.files?.[0]; e.currentTarget.value = ''; if (f) { try { const d = await fileToLogoDataUrl(f); void patch(t, { logo: d }); } catch { setErr('Logo konnte nicht verarbeitet werden.'); } } }} />
                        </label>
                        {t.logo && <button style={{ ...miniBtn, color: '#DC2626', borderColor: 'rgba(239,68,68,0.4)' }} onClick={() => patch(t, { logo: null })}>Entfernen</button>}
                      </div>
                    </div>

                    <TenantAdmins tenantId={t.id} />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', borderTop: '1px solid var(--hairline-soft)', paddingTop: 10 }}>
                      {confirmDel === t.id ? (
                        <>
                          <span style={{ fontSize: 12, color: '#DC2626' }}>Mandant unwiderruflich löschen? Alle Stelen, Gruppen, Nutzer und Call-Historie gehen verloren.</span>
                          <button onClick={() => remove(t)} disabled={busyId === t.id} style={{ ...miniBtn, color: '#fff', background: '#DC2626', border: 'none' }}>Endgültig löschen</button>
                          <button onClick={() => setConfirmDel(null)} style={miniBtn}>Abbrechen</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmDel(t.id)} style={{ ...miniBtn, color: '#DC2626', borderColor: 'rgba(239,68,68,0.4)' }}>Mandant löschen</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function TenantAdmins({ tenantId }: { tenantId: string }) {
  const [admins, setAdmins] = useState<TenantAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/superadmin/tenants/${tenantId}/admins`);
      if (r.ok) setAdmins(await r.json());
    } catch { /* */ } finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch(`/api/superadmin/tenants/${tenantId}/admins`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email, displayName: name }) });
      if (r.ok) { const a = await r.json(); setAdmins((xs) => xs.some((x) => x.id === a.id) ? xs : [...xs, a]); setEmail(''); setName(''); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Anlegen fehlgeschlagen.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={sectionTitle}>Administratoren</div>
      {loading ? <div style={{ ...mutedLine, fontSize: 12 }}>…</div> : admins.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--muted, #8aa)', marginBottom: 8 }}>Noch kein Admin.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          {admins.map((a) => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
              <span style={{ fontWeight: 600 }}>{a.displayName}</span>
              <span style={{ color: 'var(--muted, #8aa)' }}>{a.email} · {a.role}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ ...fieldCol, flex: 1, minWidth: 130 }}>
          <span style={fieldLabel}>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vorname Nachname" style={input} />
        </label>
        <label style={{ ...fieldCol, flex: 2, minWidth: 150 }}>
          <span style={fieldLabel}>E-Mail</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@kunde.de" style={input} />
        </label>
        <button onClick={add} disabled={busy || !email.trim() || name.trim().length < 2} style={{ ...miniBtn, height: 36, opacity: busy || !email.trim() || name.trim().length < 2 ? 0.5 : 1 }}>Admin anlegen</button>
      </div>
      {err && <div style={{ color: '#d8785a', fontSize: 12, marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 130, padding: '14px 18px' }}>
      <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)' }}>{label}</div>
    </div>
  );
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const shell: CSSProperties = { minHeight: '100vh', background: 'var(--bg, #0d0d0d)', color: 'var(--fg, #eee)' };
const headerBar: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid var(--hairline, #232323)' };
const contentWrap: CSSProperties = { maxWidth: 920, margin: '0 auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 16 };
const card: CSSProperties = { background: 'var(--bg-elev-1)', border: '1px solid var(--hairline, #232323)', borderRadius: 14, padding: '18px 20px' };
const fieldCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const fieldLabel: CSSProperties = { fontSize: 11.5, color: 'var(--muted, #8aa)' };
const input: CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hairline, #232323)', background: 'var(--bg-elev-2)', color: 'var(--fg, #eee)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const primaryBtn: CSSProperties = { padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--tenant-accent, #E2001A)', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', height: 36 };
const miniBtn: CSSProperties = { padding: '6px 12px', borderRadius: 7, border: '1px solid var(--hairline, #232323)', cursor: 'pointer', background: 'transparent', color: 'var(--fg, #eee)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' };
const logoutBtn: CSSProperties = { ...miniBtn };
const mutedLine: CSSProperties = { fontSize: 13, color: 'var(--muted, #8aa)' };
const sectionTitle: CSSProperties = { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--muted, #8aa)', marginBottom: 8 };
